/**
 * Import recurring items from a CSV.
 *
 * Three steps, and nothing is written before the third:
 *   1. Choose a file (or load the household list that came with the setup)
 *   2. Check the column mapping — auto-detected, always overridable
 *   3. Review every parsed row, fix what's wrong, then import
 *
 * The preview is the point. A spreadsheet's schedule column is written for a
 * human, so the importer guesses; showing exactly what it read — and letting
 * the owner change any row inline — is what makes guessing safe.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RecurringMoneyItem, TxScope } from '../../types';
import { useRecurringMoney } from '../../contexts/RecurringMoneyContext';
import { SEED_CATEGORIES, getCategoryLabel } from '../../utils/categories';
import { describeSchedule, monthlyEquivalent } from '../../utils/rhythm';
import {
  ColumnMap,
  DraftItem,
  autoMapColumns,
  downloadCsv,
  draftToItem,
  parseItemsCsv,
  parseScheduleText,
  STARTER_BILLS_CSV,
  TEMPLATE_CSV,
} from '../../utils/rhythmImport';

interface Props {
  show: boolean;
  onHide: () => void;
  existingItems: RecurringMoneyItem[];
}

type Step = 'choose' | 'map' | 'review';

const money = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n);

/** Turn a draft back into the item shape just far enough to describe its
 *  schedule with the same wording the item list uses. */
function scheduleLabel(d: DraftItem): string {
  return describeSchedule({ ...d.schedule, id: '', name: d.name, direction: d.direction, amount: d.amount, category: d.category, scope: d.scope, active: true } as RecurringMoneyItem);
}

function draftMonthly(d: DraftItem): number {
  return monthlyEquivalent(
    { ...d.schedule, id: '', name: d.name, direction: d.direction, amount: d.amount, expectedAmount: d.expectedAmount, category: d.category, scope: d.scope, active: true } as RecurringMoneyItem,
    'full'
  );
}

export function ImportItemsModal({ show, onHide, existingItems }: Props) {
  const { importItems } = useRecurringMoney();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('choose');
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [map, setMap] = useState<ColumnMap | null>(null);
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [errors, setErrors] = useState<Array<{ line: number; text: string; reason: string }>>([]);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);

  const existingNames = useMemo(() => existingItems.map((i) => i.name), [existingItems]);

  const reset = useCallback(() => {
    setStep('choose');
    setCsvText('');
    setFileName('');
    setHeaders([]);
    setMap(null);
    setDrafts([]);
    setErrors([]);
    setSkipped(new Set());
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const close = () => {
    if (importing) return;
    reset();
    onHide();
  };

  /** Parse just far enough to learn the headers, then auto-map. */
  const loadCsv = useCallback(
    (text: string, name: string) => {
      const firstLine = text.split(/\r?\n/)[0] || '';
      if (!firstLine.trim()) {
        toast.error('That file looks empty.');
        return;
      }
      const detectedHeaders = firstLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      const autoMap = autoMapColumns(detectedHeaders);
      setCsvText(text);
      setFileName(name);
      setHeaders(detectedHeaders);
      setMap(autoMap);

      // When the essentials mapped themselves, skip straight to the review —
      // the mapping step is only interesting when the guess failed.
      if (autoMap.name && autoMap.amount) {
        const parsed = parseItemsCsv(text, autoMap, existingNames);
        setDrafts(parsed.drafts);
        setErrors(parsed.errors);
        setSkipped(new Set());
        setStep('review');
      } else {
        setStep('map');
      }
    },
    [existingNames]
  );

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try {
      const text = await file.text();
      loadCsv(text, file.name);
    } catch {
      toast.error('Could not read that file.');
    }
  };

  const applyMapping = () => {
    if (!map || !csvText) return;
    if (!map.name || !map.amount) {
      toast.error('Point at least the name and amount columns at something.');
      return;
    }
    const parsed = parseItemsCsv(csvText, map, existingNames);
    setDrafts(parsed.drafts);
    setErrors(parsed.errors);
    setSkipped(new Set());
    setStep('review');
  };

  const patchDraft = (line: number, patch: Partial<DraftItem>) => {
    setDrafts((prev) => prev.map((d) => (d.line === line ? { ...d, ...patch } : d)));
  };

  const toggleSkip = (line: number) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });
  };

  const selected = useMemo(() => drafts.filter((d) => !skipped.has(d.line)), [drafts, skipped]);

  const totals = useMemo(() => {
    let monthlyOut = 0;
    let monthlyIn = 0;
    for (const d of selected) {
      const m = draftMonthly(d);
      if (d.direction === 'in') monthlyIn += m;
      else monthlyOut += m;
    }
    return { monthlyIn, monthlyOut };
  }, [selected]);

  const duplicateCount = useMemo(() => selected.filter((d) => d.duplicateOf).length, [selected]);

  const handleImport = async () => {
    if (selected.length === 0) return;
    setImporting(true);
    try {
      const count = await importItems(selected.map(draftToItem));
      toast.success(`Imported ${count} item${count === 1 ? '' : 's'}`);
      reset();
      onHide();
    } catch (err: any) {
      toast.error(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} size="xl" backdrop={importing ? 'static' : true} scrollable>
      <Modal.Header closeButton={!importing}>
        <Modal.Title>
          Import recurring items
          {fileName && <span className="text-muted fs-6 fw-normal ms-2">{fileName}</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ---------------------------------------------------- step 1 */}
        {step === 'choose' && (
          <>
            <p className="text-muted">
              A CSV with one row per bill. Column names are detected automatically — a sheet headed
              <code className="mx-1">Bill, Amount, Date, Potentially</code> needs no mapping at all.
            </p>

            <div className="d-flex flex-column gap-3">
              <div className="border rounded p-4 text-center">
                <i className="bi bi-filetype-csv fs-2x text-muted d-block mb-2" />
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="form-control"
                  onChange={(e) => handleFile(e.target.files?.[0] || null)}
                />
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <button
                  className="btn btn-sm btn-light-primary"
                  onClick={() => loadCsv(STARTER_BILLS_CSV, 'Household bills (supplied)')}
                >
                  <i className="bi bi-house-door me-1" /> Load the household bill list
                </button>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => downloadCsv('recurring-items-template.csv', TEMPLATE_CSV)}
                >
                  <i className="bi bi-download me-1" /> Download a blank template
                </button>
              </div>

              <div className="text-muted fs-8">
                The household list is the spreadsheet supplied during setup — 21 bills, amounts exactly as
                given. It still goes through the review step below; nothing is saved until you confirm.
              </div>
            </div>
          </>
        )}

        {/* ---------------------------------------------------- step 2 */}
        {step === 'map' && map && (
          <>
            <div className="alert alert-warning py-2 px-3">
              Couldn't work out which columns to use. Point them at the right ones.
            </div>
            <div className="row g-3">
              {([
                ['name', 'Name', true],
                ['amount', 'Amount', true],
                ['expectedAmount', 'Usually / "Potentially"', false],
                ['schedule', 'When it falls due', false],
                ['direction', 'Direction (in / out)', false],
                ['category', 'Category', false],
                ['scope', 'Books', false],
              ] as Array<[keyof ColumnMap, string, boolean]>).map(([field, label, required]) => (
                <div className="col-12 col-md-6" key={field}>
                  <label className="form-label fw-semibold mb-1">
                    {label} {required && <span className="text-danger">*</span>}
                  </label>
                  <select
                    className="form-select"
                    value={map[field] || ''}
                    onChange={(e) => setMap({ ...map, [field]: e.target.value || null })}
                  >
                    <option value="">— not in this file —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ---------------------------------------------------- step 3 */}
        {step === 'review' && (
          <>
            <div className="d-flex flex-wrap gap-4 align-items-center mb-3">
              <div>
                <div className="text-muted fs-8 text-uppercase fw-semibold">Importing</div>
                <div className="fs-4 fw-bold">
                  {selected.length}
                  <span className="text-muted fs-7 fw-normal"> of {drafts.length} rows</span>
                </div>
              </div>
              <div>
                <div className="text-muted fs-8 text-uppercase fw-semibold">Adds per month</div>
                <div className="fs-4 fw-bold text-danger">{money(totals.monthlyOut)}</div>
              </div>
              {totals.monthlyIn > 0 && (
                <div>
                  <div className="text-muted fs-8 text-uppercase fw-semibold">Income per month</div>
                  <div className="fs-4 fw-bold text-success">{money(totals.monthlyIn)}</div>
                </div>
              )}
            </div>

            {duplicateCount > 0 && (
              <div className="alert alert-warning py-2 px-3">
                <i className="bi bi-exclamation-triangle me-1" />
                {duplicateCount} row{duplicateCount === 1 ? '' : 's'} match the name of something you already have.
                Importing creates a second copy — untick any you meant as an edit.
              </div>
            )}

            {drafts.some((d) => d.scheduleUnreadable) && (
              <div className="alert alert-light border py-2 px-3">
                <i className="bi bi-info-circle me-1" />
                Some rows had a schedule we couldn't read. They default to "monthly, spread" and are
                highlighted — set them properly below or fix them after importing.
              </div>
            )}

            {errors.length > 0 && (
              <details className="mb-3">
                <summary className="text-danger fw-semibold" style={{ cursor: 'pointer' }}>
                  {errors.length} row{errors.length === 1 ? '' : 's'} skipped
                </summary>
                <ul className="mt-2 mb-0 ps-3 fs-8 text-muted">
                  {errors.map((e) => (
                    <li key={e.line}>
                      Line {e.line}: {e.text || '(blank)'} — {e.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="table-responsive">
              <table className="table table-sm table-row-bordered align-middle">
                <thead>
                  <tr className="fw-semibold text-muted fs-8 text-uppercase">
                    <th style={{ width: 34 }} />
                    <th>Name</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Usually</th>
                    <th>Schedule</th>
                    <th>Category</th>
                    <th>Books</th>
                    <th className="text-end">Per month</th>
                  </tr>
                </thead>
                <tbody>
                  {drafts.map((d) => {
                    const isSkipped = skipped.has(d.line);
                    return (
                      <tr key={d.line} className={isSkipped ? 'opacity-50' : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!isSkipped}
                            onChange={() => toggleSkip(d.line)}
                          />
                        </td>
                        <td>
                          <div className="fw-semibold">{d.name}</div>
                          {d.duplicateOf && (
                            <span className="badge badge-light-warning fs-9">already have "{d.duplicateOf}"</span>
                          )}
                        </td>
                        <td className="text-end">{money(d.amount)}</td>
                        <td className="text-end text-muted">
                          {d.expectedAmount === undefined ? (
                            <span className="opacity-50">same</span>
                          ) : (
                            money(d.expectedAmount)
                          )}
                        </td>
                        <td style={{ minWidth: 170 }}>
                          <input
                            className={`form-control form-control-sm ${d.scheduleUnreadable ? 'border-warning' : ''}`}
                            value={d.scheduleRaw}
                            title={scheduleLabel(d)}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const parsed = parseScheduleText(raw);
                              patchDraft(d.line, {
                                scheduleRaw: raw,
                                schedule: parsed || { kind: 'monthly_spread' },
                                scheduleUnreadable: !parsed,
                              });
                            }}
                          />
                          <div className="fs-9 text-muted">{scheduleLabel(d)}</div>
                        </td>
                        <td style={{ minWidth: 160 }}>
                          <select
                            className="form-select form-select-sm"
                            value={d.category}
                            onChange={(e) => patchDraft(d.line, { category: e.target.value })}
                          >
                            {SEED_CATEGORIES.filter((c) =>
                              d.direction === 'in' ? c.bucket === 'income' : c.bucket === 'expense'
                            ).map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.label}
                              </option>
                            ))}
                            {!SEED_CATEGORIES.some((c) => c.id === d.category) && (
                              <option value={d.category}>{getCategoryLabel(d.category)}</option>
                            )}
                          </select>
                        </td>
                        <td style={{ minWidth: 110 }}>
                          <select
                            className="form-select form-select-sm"
                            value={d.scope}
                            onChange={(e) => patchDraft(d.line, { scope: e.target.value as TxScope })}
                          >
                            <option value="personal">Family</option>
                            <option value="business">Business</option>
                          </select>
                        </td>
                        <td className="text-end text-muted">{money(draftMonthly(d))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {step !== 'choose' && (
            <button className="btn btn-sm btn-light" onClick={reset} disabled={importing}>
              <i className="bi bi-arrow-left me-1" /> Start over
            </button>
          )}
        </div>
        <div>
          <button className="btn btn-light me-2" onClick={close} disabled={importing}>
            Cancel
          </button>
          {step === 'map' && (
            <button className="btn btn-primary" onClick={applyMapping}>
              Preview rows
            </button>
          )}
          {step === 'review' && (
            <button className="btn btn-primary" onClick={handleImport} disabled={importing || selected.length === 0}>
              {importing ? 'Importing…' : `Import ${selected.length} item${selected.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
